// =====================================================
// 层级列表组件
// 根据当前层级显示不同的内容
// =====================================================

import React from 'react';
import { Plus, Monitor, Package, Cpu } from 'lucide-react';
import { useHierarchyStore } from '../../stores/hierarchy-store';
import { SeriesCard } from './SeriesCard';
import { FamilyCard } from './FamilyCard';
import { CategoryLabels, CategoryColors } from '../../types/hierarchy';
import type { ProductCategory } from '../../types/hierarchy';

// =====================================================
// 分类选择卡片
// =====================================================

interface CategoryCardProps {
  category: ProductCategory;
  onClick: () => void;
}

function CategoryCard({ category, onClick }: CategoryCardProps) {
  const colors = CategoryColors[category];
  const icons: Record<ProductCategory, React.ReactNode> = {
    all_in_one: <Monitor className="w-8 h-8" />,
    product: <Package className="w-8 h-8" />,
    module: <Cpu className="w-8 h-8" />,
  };

  return (
    <button
      onClick={onClick}
      className={`
        flex flex-col items-center justify-center gap-3 p-6 rounded-xl
        border-2 border-transparent hover:border-blue-200
        transition-all duration-200 hover:shadow-lg
        ${colors.bg}
      `}
    >
      <div className={colors.text}>{icons[category]}</div>
      <span className={`font-semibold ${colors.text}`}>
        {CategoryLabels[category]}
      </span>
    </button>
  );
}

// =====================================================
// 主组件
// =====================================================

export function HierarchyList() {
  const {
    selectedCategory,
    selectedSeries,
    selectedFamily,
    selectedModel,
    seriesList,
    familiesList,
    modelsList,
    skusList,
    isLoading,
    navigateToCategory,
    navigateToSeries,
    navigateToFamily,
    navigateToModel,
  } = useHierarchyStore();

  // Loading 状态
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Level 1: 显示分类选择
  if (!selectedCategory) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">选择产品分类</h2>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {(['all_in_one', 'product', 'module'] as ProductCategory[]).map((category) => (
            <CategoryCard
              key={category}
              category={category}
              onClick={() => navigateToCategory(category)}
            />
          ))}
        </div>
      </div>
    );
  }

  // Level 2: 显示系列列表
  if (!selectedSeries) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {CategoryLabels[selectedCategory]} - 产品系列
          </h2>
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Plus className="w-4 h-4" />
            添加系列
          </button>
        </div>
        {seriesList.length === 0 ? (
          <EmptyState message="暂无产品系列" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {seriesList.map((series) => (
              <SeriesCard
                key={series.id}
                series={series}
                onClick={() => navigateToSeries(series)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Level 3: 显示产品族列表
  if (!selectedFamily) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {selectedSeries.name} - 产品族
          </h2>
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Plus className="w-4 h-4" />
            添加产品族
          </button>
        </div>
        {familiesList.length === 0 ? (
          <EmptyState message="暂无产品族" />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {familiesList.map((family) => (
              <FamilyCard
                key={family.id}
                family={family}
                onClick={() => navigateToFamily(family)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Level 4: 显示型号列表
  if (!selectedModel) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {selectedFamily.name} - 产品型号
          </h2>
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <Plus className="w-4 h-4" />
            添加型号
          </button>
        </div>
        {modelsList.length === 0 ? (
          <EmptyState message="暂无产品型号" />
        ) : (
          <div className="space-y-3">
            {modelsList.map((model) => (
              <ModelCard
                key={model.id}
                model={model}
                onClick={() => navigateToModel(model)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Level 5: 显示 SKU 列表
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          {selectedModel.name} - SKU 变体
        </h2>
        <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          <Plus className="w-4 h-4" />
          添加 SKU
        </button>
      </div>
      {skusList.length === 0 ? (
        <EmptyState message="暂无 SKU 变体" />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {skusList.map((sku) => (
            <SKUCard key={sku.id} sku={sku} />
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================
// 型号卡片
// =====================================================

interface ModelCardProps {
  model: any;
  onClick: () => void;
}

function ModelCard({ model, onClick }: ModelCardProps) {
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-blue-200 transition-all cursor-pointer"
    >
      <div className="flex items-center gap-4">
        {/* 图片 */}
        <div className="w-20 h-14 bg-gray-900 rounded-lg overflow-hidden flex-shrink-0">
          {model.image_url ? (
            <img
              src={model.image_url}
              alt={model.name}
              className="w-full h-full object-contain"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-600">
              <Package className="w-6 h-6" />
            </div>
          )}
        </div>

        {/* 信息 */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">{model.name}</h3>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
            {model.pitch && (
              <span className="font-medium text-blue-600">P{model.pitch}</span>
            )}
            {model.led_type && <span>{model.led_type}</span>}
            {model.maintenance && <span>{model.maintenance}</span>}
          </div>
        </div>

        {/* SKU 数量 */}
        <div className="text-sm text-gray-400">
          {model.sku_count || 0} SKUs
        </div>
      </div>
    </div>
  );
}

// =====================================================
// SKU 卡片
// =====================================================

interface SKUCardProps {
  sku: any;
}

function SKUCard({ sku }: SKUCardProps) {
  return (
    <div className={`
      bg-white rounded-lg border p-3
      ${sku.is_default ? 'border-blue-300 ring-1 ring-blue-100' : 'border-gray-200'}
    `}>
      <div className="flex items-start justify-between mb-2">
        <h4 className="font-medium text-gray-900 text-sm">{sku.model_name}</h4>
        {sku.is_default && (
          <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
            默认
          </span>
        )}
      </div>
      <div className="space-y-1 text-xs text-gray-500">
        {sku.brightness_typical && (
          <p>亮度: {sku.brightness_typical}nit</p>
        )}
        {(sku.panel_width && sku.panel_height) && (
          <p>箱体: {sku.panel_width}×{sku.panel_height}mm</p>
        )}
        {sku.refresh_rate && (
          <p>刷新率: {sku.refresh_rate}Hz</p>
        )}
      </div>
    </div>
  );
}

// =====================================================
// 空状态
// =====================================================

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
      <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
      <p className="text-gray-500">{message}</p>
    </div>
  );
}

export default HierarchyList;
